/**
 * TLF Analytics - Custom analytics tracking for The Lotto Factory
 * Sends events to our own API for organization-specific analytics
 */

import { getConfig } from '../config/environment.js';

// Session management
let sessionId = null;
let analyticsEnabled = true;

// UTM attribution — captured on first page load, injected into every event
// so attribution survives across page navigations within the same session.
let _utmAttribution = null;

const UTM_STORAGE_KEY = 'tlf_utm_attribution';

/**
 * Reads UTM params from the current URL (or sessionStorage if already captured).
 * Returns null if no UTM params were ever present in this session.
 */
const getUtmAttribution = () => {
  if (_utmAttribution === null) {
    // Check sessionStorage first (persists across SPA navigations)
    try {
      const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
      if (stored) {
        _utmAttribution = JSON.parse(stored);
        return _utmAttribution;
      }
    } catch { /* ignore */ }

    // Parse from current URL
    const params = new URLSearchParams(window.location.search);
    let source = params.get('utm_source');
    let medium = params.get('utm_medium');
    let campaign = params.get('utm_campaign');
    let term = params.get('utm_term');
    let content = params.get('utm_content');

    // Fallback: the SSR partner-redirect (server/index.js) strips the UTM
    // query string when redirecting /partner/<code>?utm_source=... -> '/'.
    // The server stashes the UTMs in a non-httpOnly 'tlf_utm' cookie that
    // we can read here. We clear it immediately so it can't leak into
    // unrelated sessions. Only used when the URL didn't already carry UTMs.
    if (!source && !medium && !campaign) {
      try {
        const cookieMatch = document.cookie
          .split('; ')
          .find((row) => row.startsWith('tlf_utm='));
        if (cookieMatch) {
          const cookieParams = new URLSearchParams(
            decodeURIComponent(cookieMatch.split('=')[1] || '')
          );
          source = source || cookieParams.get('utm_source');
          medium = medium || cookieParams.get('utm_medium');
          campaign = campaign || cookieParams.get('utm_campaign');
          term = term || cookieParams.get('utm_term');
          content = content || cookieParams.get('utm_content');
          // Clear the cookie so it doesn't leak / get re-applied on refresh.
          document.cookie = 'tlf_utm=; Max-Age=0; path=/; SameSite=Lax';
        }
      } catch { /* ignore */ }
    }

    // Also attribute partner code links (e.g. ?tlf_utm=... or ?partner=...) so
    // partner referral traffic tracks in campaign/analytics reporting
    if (!source && !medium && !campaign) {
      const partnerParam = params.get('tlf_utm') || params.get('partner') || params.get('partnerCode') || params.get('code');
      if (partnerParam) {
        source = 'partner';
        medium = 'referral';
        campaign = `partner_${partnerParam}`;
      }
    }

    if (source || medium || campaign) {
      _utmAttribution = {
        utm_source: source || undefined,
        utm_medium: medium || undefined,
        utm_campaign: campaign || undefined,
        utm_term: term || undefined,
        utm_content: content || undefined,
        initial_referrer: document.referrer || undefined,
      };
      try { sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(_utmAttribution)); } catch { /* ignore */ }
      return _utmAttribution;
    }

    // Mark as "no UTM" so we don't keep checking
    _utmAttribution = false;
  }
  return _utmAttribution || null;
};

/**
 * Generates or retrieves a session ID
 * Sessions persist for 30 minutes of inactivity
 */
const getSessionId = () => {
  const SESSION_KEY = 'tlf_analytics_session';
  const SESSION_TIMESTAMP_KEY = 'tlf_analytics_session_timestamp';
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

  const now = Date.now();
  const storedTimestamp = localStorage.getItem(SESSION_TIMESTAMP_KEY);
  const storedSession = localStorage.getItem(SESSION_KEY);

  // Check if session is still valid
  if (storedSession && storedTimestamp) {
    const elapsed = now - parseInt(storedTimestamp, 10);
    if (elapsed < SESSION_TIMEOUT_MS) {
      // Update timestamp to extend session
      localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
      return storedSession;
    }
  }

  // Create new session
  const newSessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  localStorage.setItem(SESSION_KEY, newSessionId);
  localStorage.setItem(SESSION_TIMESTAMP_KEY, now.toString());
  
  return newSessionId;
};

/**
 * Gets the API base URL from window config
 */
const getApiUrl = () => {
  const { backendUrl } = getConfig();
  return backendUrl || '';
};

/**
 * Tracks an analytics event
 * @param {Object} eventData - Event data to track
 * @param {string} eventData.eventType - Type of event (PageView, ButtonClick, etc.)
 * @param {string} [eventData.pageUrl] - URL of the page
 * @param {string} [eventData.pagePath] - Path of the page
 * @param {string} [eventData.referrer] - Referrer URL
 * @param {number} [eventData.duration] - Duration in milliseconds
 * @param {Object} [eventData.customData] - Custom event data
 */
export const trackEvent = async (eventData) => {
  const { isSocialBot } = getConfig();
  
  if (!analyticsEnabled || isSocialBot === true) {
    return;
  }

  const apiUrl = getApiUrl();
  if (!apiUrl) {
    console.debug('[TLF Analytics] No API URL configured');
    return;
  }

  // Get or create session
  if (!sessionId) {
    sessionId = getSessionId();
  }

  // Collect browser/device info
  const screenResolution = window.screen 
    ? `${window.screen.width}x${window.screen.height}` 
    : null;
  const browserLanguage = navigator.language || navigator.userLanguage || null;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  const eventType = eventData.eventType || 'PageView';
  const isClientError = eventType === 'ClientError';

  const sanitizeEnvelopeUrl = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch {
      return value.split(/[?#]/, 1)[0];
    }
  };

  const payload = {
    eventType,
    sessionId,
    pageUrl: isClientError
      ? sanitizeEnvelopeUrl(eventData.pageUrl || window.location.href)
      : eventData.pageUrl || window.location.href,
    pagePath: eventData.pagePath || window.location.pathname,
    referrer: isClientError
      ? sanitizeEnvelopeUrl(eventData.referrer || document.referrer)
      : eventData.referrer || document.referrer || null,
    memberId: isClientError ? null : eventData.memberId || getMemberId(),
    duration: eventData.duration || null,
    screenResolution,
    browserLanguage,
    timezone,
    clientError: eventData.clientError || null,
    customData: isClientError
      ? {}
      : {
          ...getUtmAttribution(),
          ...(eventData.customData || {}),
        },
  };

  try {
    const response = await fetch(`${apiUrl}/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      keepalive: eventData.keepalive === true,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Log error only in development
      if (import.meta.env.DEV) {
        console.debug('[TLF Analytics] Tracking request failed:', response.status);
      }
    }
  } catch (error) {
    // Silently fail - analytics should never break the user experience
    if (import.meta.env.DEV) {
      console.debug('[TLF Analytics] Tracking error:', error);
    }
  }
};

/**
 * Tracks a page view event
 * @param {string} [pathname] - Page path (defaults to current path)
 * @param {Object} [customData] - Additional custom data
 */
export const trackPageView = (pathname = null, customData = null) => {
  trackEvent({
    eventType: 'PageView',
    pagePath: pathname || window.location.pathname,
    pageUrl: window.location.href,
    referrer: document.referrer,
    customData
  });
};

/**
 * Tracks when a user views the login modal/page
 * @param {Object} [customData] - Additional custom data (e.g., authMethods available)
 */
export const trackLoginView = (customData = null) => {
  trackEvent({
    eventType: 'LoginView',
    pagePath: window.location.pathname,
    customData
  });
};

/**
 * Tracks when a user views the register/signup modal/page
 * @param {Object} [customData] - Additional custom data (e.g., authMethods available)
 */
export const trackRegisterView = (customData = null) => {
  trackEvent({
    eventType: 'RegisterView',
    pagePath: window.location.pathname,
    customData
  });
};

/**
 * Tracks when a user is shown the geo-block/excluded page
 * Location data is automatically enriched by the API from the client IP
 */
export const trackExcluded = () => {
  trackEvent({
    eventType: 'Excluded',
    pagePath: '/excluded'
  });
};

/**
 * Tracks a successful login
 * @param {string} authMethod - The authentication method used (email, facebook, google)
 * @param {Object} [customData] - Additional custom data
 */
export const trackLoginSuccess = (authMethod, customData = null) => {
  trackEvent({
    eventType: 'LoginSuccess',
    customData: {
      authMethod,
      ...customData
    }
  });
};

/**
 * Tracks a failed login attempt
 * @param {string} authMethod - The authentication method attempted (email, facebook, google)
 * @param {string} [reason] - Reason for failure
 * @param {Object} [customData] - Additional custom data
 */
export const trackLoginFailed = (authMethod, reason = null, customData = null) => {
  trackEvent({
    eventType: 'LoginFailed',
    customData: {
      authMethod,
      reason,
      ...customData
    }
  });
};

/**
 * Tracks a successful registration
 * @param {string} authMethod - The authentication method used (email, facebook, google)
 * @param {Object} [customData] - Additional custom data
 */
export const trackRegisterSuccess = (authMethod, customData = null) => {
  trackEvent({
    eventType: 'RegisterSuccess',
    customData: {
      authMethod,
      ...customData
    }
  });
};

/**
 * Tracks a failed registration attempt
 * @param {string} authMethod - The authentication method attempted (email, facebook, google)
 * @param {string} [reason] - Reason for failure
 * @param {Object} [customData] - Additional custom data
 */
export const trackRegisterFailed = (authMethod, reason = null, customData = null) => {
  trackEvent({
    eventType: 'RegisterFailed',
    customData: {
      authMethod,
      reason,
      ...customData
    }
  });
};

/**
 * Tracks a button click event
 * @param {string} buttonId - Identifier for the button
 * @param {string} [buttonText] - Text of the button
 * @param {Object} [customData] - Additional custom data
 */
export const trackButtonClick = (buttonId, buttonText = null, customData = null) => {
  trackEvent({
    eventType: 'ButtonClick',
    customData: {
      buttonId,
      buttonText,
      ...customData
    }
  });
};

/**
 * Tracks a form submission event
 * @param {string} formId - Identifier for the form
 * @param {boolean} success - Whether the submission was successful
 * @param {Object} [customData] - Additional custom data
 */
export const trackFormSubmission = (formId, success, customData = null) => {
  trackEvent({
    eventType: success ? 'FormSubmitSuccess' : 'FormSubmitError',
    customData: {
      formId,
      ...customData
    }
  });
};

/**
 * Tracks a purchase event
 * @param {Object} purchaseData - Purchase details
 * @param {string} purchaseData.transactionId - Transaction ID
 * @param {number} purchaseData.amount - Purchase amount
 * @param {string} purchaseData.currency - Currency code
 * @param {string} purchaseData.productType - Type of product (ticket, subscription)
 */
export const trackPurchase = (purchaseData) => {
  trackEvent({
    eventType: 'Purchase',
    customData: {
      transactionId: purchaseData.transactionId,
      amount: purchaseData.amount,
      currency: purchaseData.currency,
      productType: purchaseData.productType
    }
  });
};

/**
 * Tracks when a user starts the checkout process
 * @param {Object} [customData] - Additional custom data
 */
export const trackCheckoutStart = (customData = null) => {
  trackEvent({
    eventType: 'CheckoutStart',
    customData
  });
};

/**
 * Tracks when a user views a lottery/draw page
 * @param {Object} lotteryData - Lottery details
 * @param {string} lotteryData.lotteryId - Lottery ID
 * @param {string} lotteryData.lotteryType - Type of lottery (5050, ChaseTheAce, etc.)
 * @param {string} [lotteryData.lotteryName] - Name of the lottery
 * @param {number} [lotteryData.drawNumber] - Draw number
 */
export const trackViewLottery = (lotteryData) => {
  trackEvent({
    eventType: 'ViewLottery',
    customData: {
      lotteryId: lotteryData.lotteryId,
      lotteryType: lotteryData.lotteryType,
      lotteryName: lotteryData.lotteryName,
      drawNumber: lotteryData.drawNumber
    }
  });
};

/**
 * Tracks when a user adds tickets to cart
 * @param {Object} cartData - Cart details
 * @param {string} cartData.lotteryId - Lottery ID
 * @param {string} cartData.lotteryType - Type of lottery
 * @param {number} cartData.ticketCount - Number of tickets added
 * @param {number} cartData.amount - Total amount
 * @param {string} [cartData.currency] - Currency code
 */
export const trackAddToCart = (cartData) => {
  trackEvent({
    eventType: 'AddToCart',
    customData: {
      lotteryId: cartData.lotteryId,
      lotteryType: cartData.lotteryType,
      ticketCount: cartData.ticketCount,
      amount: cartData.amount,
      currency: cartData.currency || 'CAD'
    }
  });
};

/**
 * Tracks when a user selects a card in Chase the Ace
 * @param {Object} cardData - Card selection details
 * @param {string} cardData.lotteryId - Lottery ID
 * @param {string} cardData.cardId - Selected card ID
 * @param {number} cardData.ticketCount - Number of tickets
 * @param {number} cardData.amount - Total amount
 */
export const trackSelectCard = (cardData) => {
  trackEvent({
    eventType: 'SelectCard',
    customData: {
      lotteryId: cardData.lotteryId,
      cardId: cardData.cardId,
      ticketCount: cardData.ticketCount,
      amount: cardData.amount
    }
  });
};

/**
 * Tracks when a user begins the checkout process
 * @param {Object} checkoutData - Checkout details
 * @param {number} checkoutData.cartTotal - Total cart value
 * @param {number} checkoutData.ticketCount - Total ticket count
 * @param {string} [checkoutData.lotteryType] - Type of lottery
 */
export const trackBeginCheckout = (checkoutData) => {
  trackEvent({
    eventType: 'BeginCheckout',
    customData: {
      cartTotal: checkoutData.cartTotal,
      ticketCount: checkoutData.ticketCount,
      lotteryType: checkoutData.lotteryType
    }
  });
};

/**
 * Tracks page engagement (time spent on page)
 * Should be called before navigation or page unload
 * @param {number} duration - Time spent in milliseconds
 * @param {Object} [customData] - Additional custom data
 */
export const trackEngagement = (duration, customData = null) => {
  trackEvent({
    eventType: 'PageEngagement',
    pagePath: customData?.pathname || window.location.pathname,
    duration,
    customData
  });
};

/**
 * Tracks an error event
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {Object} [customData] - Additional custom data
 */
export const trackError = (errorType, errorMessage, customData = null) => {
  trackEvent({
    eventType: 'Error',
    customData: {
      errorType,
      errorMessage,
      ...customData
    }
  });
};

/**
 * Tracks a browser runtime failure. The API validates and fingerprints this payload.
 * @param {Object} clientError - Sanitized client error contract
 */
export const trackClientError = (clientError) => trackEvent({
  eventType: 'ClientError',
  clientError,
  keepalive: true
});

/**
 * Gets the member ID from local storage if available
 */
const getMemberId = () => {
  try {
    const memberData = localStorage.getItem('access_token');
    if (memberData) {
      // Try to parse JWT token to get member ID
      const payload = JSON.parse(atob(memberData.split('.')[1]));
      return payload.sub || payload.id || payload.memberId || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

/**
 * Enables analytics tracking
 */
export const enableAnalytics = () => {
  analyticsEnabled = true;
};

/**
 * Disables analytics tracking (e.g., for GDPR compliance)
 */
export const disableAnalytics = () => {
  analyticsEnabled = false;
};

/**
 * Checks if analytics is currently enabled
 * @returns {boolean}
 */
export const isAnalyticsEnabled = () => {
  return analyticsEnabled;
};

export default {
  trackEvent,
  trackPageView,
  trackLoginView,
  trackRegisterView,
  trackExcluded,
  trackLoginSuccess,
  trackLoginFailed,
  trackRegisterSuccess,
  trackRegisterFailed,
  trackButtonClick,
  trackFormSubmission,
  trackPurchase,
  trackCheckoutStart,
  trackViewLottery,
  trackAddToCart,
  trackSelectCard,
  trackBeginCheckout,
  trackEngagement,
  trackError,
  trackClientError,
  enableAnalytics,
  disableAnalytics,
  isAnalyticsEnabled
};
